/*
 * LUMO water hardness checker
 *
 * Postcode area (outward-code letters) -> typical hardness band + representative mg/L.
 * Tier 1: area-level mapping, no external API. Always presented as "typical for your area".
 *
 * Bands (standard UK classification, mg/L CaCO3):
 *   soft       < 100
 *   moderate   100-200
 *   hard       200-300
 *   very hard  > 300
 *
 * Sources: DWI classification + regional water-company hardness ranges (geology-based).
 * Mixed-geology areas are assigned their most common band; the tool labels all figures
 * as typical/estimated for the area.
 */

(function () {
  var WC_MAX_PPM = 400; // gauge top = realistic UK absolute maximum

  // Representative hardness (mg/L) per postcode AREA (the letters before the digits).
  // Chosen as a typical mid-value for that area's supply; label always says "typical".
  var AREA_PPM = {
    // --- London / South East (chalk, very hard) ---
    E: 340, EC: 340, N: 340, NW: 330, SE: 325, SW: 340, W: 340, WC: 340,
    BR: 320, CR: 340, DA: 350, EN: 340, HA: 340, IG: 350, KT: 320, RM: 360,
    SM: 340, TW: 320, UB: 340, WD: 360,
    // Home counties / South East
    AL: 360, HP: 350, LU: 370, SG: 360, MK: 330, OX: 330, RG: 330, SL: 340,
    GU: 250, ME: 330, CT: 330, TN: 240, BN: 270, RH: 240, PO: 270, SO: 260,
    // --- East of England / Anglian (chalk, hard-very hard) ---
    CB: 360, CM: 340, CO: 350, IP: 340, NR: 360, PE: 370, SS: 350,
    // --- East Midlands ---
    LE: 260, NG: 250, DE: 240, LN: 300, NN: 300,
    // --- West Midlands (Severn Trent, hard) ---
    B: 250, CV: 250, DY: 240, WV: 240, WS: 250, WR: 250, HR: 230, ST: 230,
    TF: 230,
    // --- South West (mixed) ---
    BS: 260, BA: 270, GL: 280, SN: 300, SP: 300, DT: 260, BH: 250, TA: 250,
    EX: 180, PL: 90, TQ: 150, TR: 60, // Cornwall/Devon granite -> softer
    // --- Wales (soft) ---
    CF: 70, NP: 90, SA: 60, LD: 70, LL: 50, SY: 120, CH: 200,
    // --- North West (United Utilities, soft) ---
    M: 70, BL: 70, BB: 70, PR: 70, FY: 80, WA: 90, WN: 70, OL: 70, SK: 90,
    L: 80, CW: 200, LA: 70, CA: 60,
    // --- Yorkshire & North East (mixed; typical) ---
    LS: 130, BD: 90, HD: 90, HX: 90, WF: 140, HG: 140, YO: 200, HU: 300,
    DN: 260, S: 120, DL: 130, TS: 140, SR: 130, NE: 120, DH: 130,
    // --- Scotland (soft) ---
    AB: 40, DD: 40, DG: 50, EH: 50, FK: 40, G: 30, HS: 30, IV: 40, KA: 40,
    KW: 50, KY: 45, ML: 40, PA: 40, PH: 40, TD: 90, ZE: 40,
    // --- Northern Ireland (soft-moderate) ---
    BT: 100,
  };

  var BANDS = {
    soft: { label: "soft", color: "var(--wc-soft)", min: 0, max: 99 },
    moderate: { label: "moderately hard", color: "var(--wc-moderate)", min: 100, max: 199 },
    hard: { label: "hard", color: "var(--wc-hard)", min: 200, max: 299 },
    veryhard: { label: "very hard", color: "var(--wc-veryhard)", min: 300, max: 9999 },
  };

  function bandFromPpm(ppm) {
    if (ppm < 100) return "soft";
    if (ppm < 200) return "moderate";
    if (ppm < 300) return "hard";
    return "veryhard";
  }

  // Extract the AREA (leading letters) from a UK postcode / outcode.
  function areaFromPostcode(raw) {
    if (!raw) return null;
    var pc = raw.toUpperCase().replace(/\s+/g, "");
    var m = pc.match(/^[A-Z]{1,2}/);
    if (!m) return null;
    var letters = m[0];
    // Two-letter area may actually be a one-letter area + digit-letter; check table.
    if (AREA_PPM[letters] !== undefined) return letters;
    if (AREA_PPM[letters[0]] !== undefined) return letters[0];
    return null;
  }

  function isValidPostcodeStart(raw) {
    // Lenient: accept a full postcode or just an outcode.
    return /^[A-Z]{1,2}\d/i.test((raw || "").trim());
  }

  function emailValid(email) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  }

  // % of UK areas softer than this ppm (for the "harder than X%" line).
  function percentileSofterThan(ppm) {
    var values = Object.keys(AREA_PPM).map(function (k) { return AREA_PPM[k]; });
    var softer = values.filter(function (v) { return v < ppm; }).length;
    return Math.round((softer / values.length) * 100);
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animateGauge(ppmEl, markerEl, fillEl, targetPpm, targetPct, reduced, knobEl) {
    if (reduced) {
      ppmEl.textContent = targetPpm + " ppm";
      markerEl.style.left = "calc(" + (targetPct / 100) + " * (100% - 22px) + 11px)";
      if (fillEl) fillEl.style.width = targetPct + "%";
      if (knobEl) knobEl.style.left = "calc(" + (targetPct / 100) + " * (100% - 22px) + 11px)";
      return;
    }
    var duration = 2000;
    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var t = Math.min((ts - start) / duration, 1);
      var e = easeOutCubic(t);
      ppmEl.textContent = Math.round(targetPpm * e) + " ppm";
      markerEl.style.left = "calc(" + (targetPct * e / 100) + " * (100% - 22px) + 11px)";
      if (fillEl) fillEl.style.width = (targetPct * e) + "%";
      if (knobEl) knobEl.style.left = "calc(" + (targetPct * e / 100) + " * (100% - 22px) + 11px)"; 
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  customElements.define(
    "water-checker",
    class WaterChecker extends HTMLElement {
      connectedCallback() {
        this.klaviyoCompany = this.dataset.klaviyoCompany || "";
        this.klaviyoList = this.dataset.klaviyoList || "";

        this.recMap = {
          soft: {
            primaryKey: "acf",
            cadence: this.dataset.cadenceSoft || "every 4 months",
            upsell: true,
          },
          moderate: {
            primaryKey: "acf",
            cadence: this.dataset.cadenceModerate || "every 3 months",
            upsell: true,
          },
          hard: {
            primaryKey: "antiscale",
            cadence: this.dataset.cadenceHard || "every 8 weeks",
            upsell: false,
          },
          veryhard: {
            primaryKey: "antiscale",
            cadence: this.dataset.cadenceVeryhard || "every 6 weeks",
            upsell: false,
          },
        };

        this.urls = {
          acf: this.dataset.urlAcf || "",
          vc: this.dataset.urlVc || "",
          antiscale: this.dataset.urlAntiscale || "",
        };
        this.names = {
          acf: "ACF Alone",
          vc: "VC + ACF",
          antiscale: "Anti-scale + ACF",
        };

        this.form = this.querySelector("[data-wc-form]");
        this.postcodeInput = this.querySelector("[data-wc-postcode]");
        this.emailInput = this.querySelector("[data-wc-email]");
        this.consentInput = this.querySelector("[data-wc-consent]");
        this.errorEl = this.querySelector("[data-wc-error]");
        this.resultEl = this.querySelector("[data-wc-result]");
        this.submitBtn = this.querySelector("[data-wc-submit]");

        this.form.addEventListener("submit", (e) => {
          e.preventDefault();
          this.onSubmit();
        });
      }

      onSubmit() {
        this.errorEl.textContent = "";
        var pc = this.postcodeInput.value.trim();

        if (this.dataset.testMode === "true") {
          var TEST_PPM = { soft: 70, moderate: 150, hard: 250, veryhard: 340 };
          var keyword = pc.toLowerCase().replace(/\s+/g, "");
          if (keyword === "medium") keyword = "moderate";
          if (keyword === "very" || keyword === "veryhard") keyword = "veryhard";
          if (TEST_PPM[keyword] !== undefined) {
            var testPpm = TEST_PPM[keyword];
            var testBand = bandFromPpm(testPpm);
            this._lastResult = { area: "TEST", ppm: testPpm, band: testBand };
            if (this.dataset.wcVariant === "full") {
              this.showLoading();
              var testSelf = this;
              var testReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
              window.clearTimeout(this._wcTimer);
              this._wcTimer = window.setTimeout(function () {
                testSelf.renderGaugeResult("TEST", testPpm, testBand);
              }, testReduced ? 0 : 2600);
            } else {
              this.renderResult("TEST", testPpm, testBand);
            }
            return;
          }
        }

        if (!isValidPostcodeStart(pc)) {
          this.errorEl.textContent = "Please enter a valid UK postcode.";
          return;
        }
        var area = areaFromPostcode(pc);
        if (!area || AREA_PPM[area] === undefined) {
          this.errorEl.textContent =
            "We couldn't find that postcode area. Double-check and try again.";
          return;
        }

        var ppm = AREA_PPM[area];
        var band = bandFromPpm(ppm);

        this._lastResult = { area: area, ppm: ppm, band: band };

       if (this.dataset.wcVariant === "full") {
          this.showLoading();
          var self = this;
          var reducedM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          var delay = reducedM ? 0 : 2600;
          window.clearTimeout(this._wcTimer);
          this._wcTimer = window.setTimeout(function () {
            self.renderGaugeResult(area, ppm, band);
          }, delay);
        } else {
          this.renderResult(area, ppm, band);
          // Compact block variant keeps inline capture on the single form.
          var email = this.emailInput ? this.emailInput.value.trim() : "";
          var consented = this.consentInput ? this.consentInput.checked : false;
          if (email && emailValid(email) && consented) {
            this.subscribe(email, area, ppm, band);
          }
        }
      }

      renderResult(area, ppm, band) {
        var b = BANDS[band];
        var rec = this.recMap[band];
        var primaryName = this.names[rec.primaryKey];
        var primaryUrl = this.urls[rec.primaryKey] || "#";

        this.resultEl.innerHTML =
          '<div class="water-checker__band">' +
          '<span class="water-checker__band-dot" style="background:' + b.color + '"></span>' +
          '<span class="water-checker__band-label">' + b.label + "</span>" +
          '<span class="water-checker__ppm">~' + ppm + " mg/L</span>" +
          "</div>" +
          '<p class="water-checker__area-note">Typical for the ' + area +
          " postcode area — your exact street may vary slightly.</p>" +
          '<p class="water-checker__rec">Based on your area, we recommend the ' +
          "<strong>" + primaryName + "</strong> filter.</p>" +
          '<p class="water-checker__cadence">Suggested replacement: ' + rec.cadence + ".</p>" +
          '<a class="water-checker__cta" href="' + primaryUrl + '">View ' + primaryName + " →</a>" +
          upsellHtml;

        this.resultEl.setAttribute("data-visible", "true");
        this.resultEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }

      fadeSwap(el, newText) {
        if (!el) return;
        var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) { el.textContent = newText; return; }
        el.style.transition = "opacity 0.25s ease";
        el.style.opacity = "0";
        window.setTimeout(function () {
          el.textContent = newText;
          el.style.opacity = "1";
        }, 250);
      }

      showLoading() {
        if (this.submitBtn) this.submitBtn.disabled = true;

        // Keep the placeholder bar; just switch it into an animated "analysing" state.
        this.resultEl.classList.add("water-checker__result--loading");
        this.resultEl.setAttribute("data-visible", "true");

        var eyebrow = this.resultEl.querySelector(".wc-gauge__eyebrow");
        var heading = this.resultEl.querySelector(".wc-gauge__band");

        var wrap = this.querySelector(".wc-section__result-wrap") || this.resultEl;
        wrap.scrollIntoView({ behavior: "smooth", block: "start" });

        if (heading) {
          heading.classList.remove("wc-cta__heading--typing");
          this.fadeSwap(heading, "Analysing your area\u2026");
        }
      }

      buildCta(band) {
        var key = band.charAt(0).toUpperCase() + band.slice(1);
        var url = this.dataset["ctaUrl" + key] || "";
        var eyebrow = this.dataset.ctaEyebrow || "";
        var heading = this.dataset["ctaHeading" + key] || "";
        var label = this.dataset.ctaButton || "Learn more";
        if (!url && !heading) return "";
        var align = this.dataset.ctaAlign || "center";
        this._ctaHeadingText = heading;
        this._ctaButtonHtml = url
          ? '<a class="wc-cta__button" href="' + url + '">' + label + "</a>"
          : "";
        var html = '<div class="wc-cta wc-cta--' + align + '" data-wc-cta>';
        if (eyebrow) html += '<p class="wc-cta__eyebrow">' + eyebrow + "</p>";
        html += '<p class="wc-cta__heading" data-wc-cta-heading></p>';
        html += '<span data-wc-cta-button-slot></span>';
        html += "</div>";
        return html;
      }

      typeCta() {
        var headingEl = this.resultEl.querySelector("[data-wc-cta-heading]");
        var slot = this.resultEl.querySelector("[data-wc-cta-button-slot]");
        if (!headingEl) return;
        var text = this._ctaHeadingText || "";
        var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        var self = this;
        function revealButton() {
          if (slot && self._ctaButtonHtml) slot.innerHTML = self._ctaButtonHtml;
        }

        if (reduced) {
          headingEl.textContent = text;
          revealButton();
          return;
        }

        headingEl.classList.add("wc-cta__heading--typing");
        var i = 0;
        window.clearInterval(this._wcTypeTimer);
        this._wcTypeTimer = window.setInterval(function () {
          headingEl.textContent = text.slice(0, i);
          i++;
          if (i > text.length) {
            window.clearInterval(self._wcTypeTimer);
            headingEl.classList.remove("wc-cta__heading--typing");
            window.setTimeout(revealButton, 500);
          }
        }, 45);
      }

      renderGaugeResult(area, ppm, band) {
        window.clearInterval(this._wcLoadTimer);
        if (this.submitBtn) this.submitBtn.disabled = false;
        this.resultEl.classList.remove("water-checker__result--loading", "water-checker__result--placeholder");
        
        var b = BANDS[band];
        var rec = this.recMap[band];
        var primaryName = this.names[rec.primaryKey];
        var primaryUrl = this.urls[rec.primaryKey] || "#";
        var pct = Math.min((ppm / WC_MAX_PPM) * 100, 100);
        var softerThan = percentileSofterThan(ppm);
        var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.resultEl.innerHTML =
          '<p class="wc-gauge__eyebrow">Your result</p>' +
          '<div class="wc-gauge__band">' + b.label + "</div>" +
          '<div class="wc-gauge__ppm" data-wc-ppm>0 ppm</div>' +
          '<div class="wc-gauge__bar">' +
          '<div class="wc-gauge__bar-fill" data-wc-fill></div>' +
          '<div class="wc-gauge__marker" data-wc-marker>' +
          '<span class="wc-gauge__marker-pill">' + b.label + "</span>" +
          "</div>" +
          "</div>" +
          '<div class="wc-gauge__track"><div class="wc-gauge__track-knob" data-wc-knob></div></div>' +
          '<div class="wc-gauge__scale"><span>0 ppm</span><span>' + WC_MAX_PPM + " ppm</span></div>" +
          '<p class="wc-gauge__stat">Your water in the <strong>' + area +
          "</strong> area is harder than <strong>" + softerThan +
          "%</strong> of UK areas, scoring <strong>~" + ppm + " ppm</strong>.</p>" +
          this.buildCta(band);

        this.resultEl.setAttribute("data-visible", "true");

        var ppmEl = this.resultEl.querySelector("[data-wc-ppm]");
        var markerEl = this.resultEl.querySelector("[data-wc-marker]");
        var fillEl = this.resultEl.querySelector("[data-wc-fill]");
        var knobEl = this.resultEl.querySelector("[data-wc-knob]");
        animateGauge(ppmEl, markerEl, fillEl, ppm, pct, reduced, knobEl);

        var self = this;
        window.clearTimeout(this._wcTypeStart);
        this._wcTypeStart = window.setTimeout(function () {
          self.typeCta();
        }, reduced ? 0 : 2000);
      }

      subscribe(email, area, ppm, band) {
        if (!this.klaviyoCompany || !this.klaviyoList) return;

        // Klaviyo client-side subscribe (Subscriptions API, public company id).
        var payload = {
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                    properties: {
                      brand: "LUMO",
                      water_band: band,
                      water_ppm: ppm,
                      postcode_area: area,
                    },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: "list", id: this.klaviyoList } },
            },
          },
        };

        fetch(
          "https://a.klaviyo.com/client/subscriptions/?company_id=" +
            encodeURIComponent(this.klaviyoCompany),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: "2024-10-15",
            },
            body: JSON.stringify(payload),
          },
        ).catch(function () {
          /* silent — never block the on-page result */
        });
      }
    },
  );
})();